/**
 * ProductImageDetail - Redesigned Product Image Modal
 *
 * Modern, professional edit modal using EditModal with:
 * - Tab-based navigation
 * - Consistent form field styling
 * - Keyboard shortcuts
 * - Unsaved changes detection
 */

import React, { useEffect, useState, useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import {
	BaseForm,
	TextBox,
} from '../../../components/base';
import { EditModal, EditPageField } from '../../../components/container';
import { ImageBox } from '../../../components/base/ImageBox';
import { ProductImageModel, dfProductImageModel, validProductImageSchema } from '../../../context/models/products-image/ProductImageModel';
import { Image as ImageIcon, FileText, Settings } from 'lucide-react';

type IProductImageDetailModalProps = {
	isShow: boolean;
	title: string;
	detailModel?: ProductImageModel;
	isSaving: boolean | false;
	onClose: () => void;
	onSubmit: (data: ProductImageModel) => void;
};

const ProductImageDetail = ({ isShow, title, detailModel, onSubmit, onClose, isSaving }: IProductImageDetailModalProps) => {
	const isAddMode = !detailModel;
	const [initialValues, setInitialValues] = useState<ProductImageModel>(dfProductImageModel);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<ProductImageModel>({
		resolver: yupResolver(validProductImageSchema() as any),
		mode: 'all',
		defaultValues: initialValues,
	});

	const onSubmitHandler = (values: ProductImageModel) => {
		const data = { ...values, inGallery: true };
		onSubmit(data);
		setHasUnsavedChanges(false);
	};

	useEffect(() => {
		if (initialValues) {
			reset(initialValues);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValues, reset]);

	useEffect(() => {
		if (detailModel) {
			setInitialValues((prev) => ({ ...prev, ...detailModel }));
			setHasUnsavedChanges(false);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [detailModel]);

	// Helper to update field and mark as unsaved
	const updateField = useCallback((field: keyof ProductImageModel, value: any) => {
		setInitialValues((prev) => ({ ...prev, [field]: value }));
		setHasUnsavedChanges(true);
	}, []);

	// Tab content: Image Info
	const infoTab = (
		<div className='space-y-4'>
			<EditPageField
				label='Tiêu đề ảnh'
				required
				error={errors.title?.message}
				helpText='Mô tả ngắn cho hình ảnh'
			>
				<TextBox
					name='title'
					error={errors.title?.message}
					onValueChanged={(value) => updateField('title', value)}
					value={initialValues.title}
					hasValid={true}
					placeholder='Nhập tiêu đề ảnh...'
				/>
			</EditPageField>

			<EditPageField
				label='Mô tả'
				error={errors.description?.message}
				helpText='Mô tả chi tiết hơn về hình ảnh'
			>
				<TextBox
					name='description'
					error={errors.description?.message}
					onValueChanged={(value) => updateField('description', value)}
					value={initialValues.description}
					hasValid={true}
					placeholder='Mô tả...'
				/>
			</EditPageField>
		</div>
	);

	// Tab content: Upload Image
	const imageTab = (
		<div className='space-y-4'>
			<EditPageField
				label='Hình ảnh'
				required
				error={errors.path?.message}
				helpText='Tải lên hình ảnh sản phẩm'
			>
				<ImageBox
					label='Upload ảnh'
					name='path'
					value={initialValues.path}
					hasValid={true}
					file_name={initialValues.title}
					clearable={false}
					onDataChanged={(image) => {
						updateField('path', image?.path || '');
						updateField('extension', image?.extension || '');
						updateField('size', image?.size || '');
					}}
				/>
			</EditPageField>

			{initialValues.path && (
				<div className='mt-4'>
					<img
						src={initialValues.path}
						alt={initialValues.title}
						className='max-w-full h-auto rounded-lg border shadow-sm'
					/>
				</div>
			)}
		</div>
	);

	const tabs = [
		{
			id: 'info',
			label: 'Thông tin ảnh',
			icon: <FileText className='h-4 w-4' />,
			content: infoTab,
		},
		{
			id: 'image',
			label: 'Hình ảnh',
			icon: <ImageIcon className='h-4 w-4' />,
			content: imageTab,
		},
	];

	return (
		<EditModal
			isOpen={isShow}
			onClose={onClose}
			title={title}
			description={isAddMode ? 'Tạo mới hình ảnh sản phẩm' : 'Chỉnh sửa hình ảnh sản phẩm'}
			sizeClass='md'
			tabs={tabs}
			isSaving={isSaving}
			onSave={handleSubmit(onSubmitHandler as any)}
			onCancel={onClose}
			hasUnsavedChanges={hasUnsavedChanges}
			breadcrumbs={[
				{ label: 'Trang chủ', href: '/' },
				{ label: 'Hình ảnh sản phẩm', href: `/products/${detailModel?.productId}/images` },
				{ label: isAddMode ? 'Tạo mới' : 'Chỉnh sửa' },
			]}
			status={isAddMode ? 'Tạo mới' : undefined}
			statusVariant='default'
		/>
	);
};

export default ProductImageDetail;
